import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { firebase } from "../../firebase/config";
import "./GoYearly.css";

// /go-yearly - the two-click switch to Premium Yearly (Paul, 25 Sep 2026: "charges them $500
// immediately, and adjusts etc does our normal routine. With minimal clicks"). Click 1 is the
// "Best value" button on the home page or Game Plan; click 2 is "Confirm" here. The charge and
// the carried-over time are done by the yearlyUpgrade cloud function
// (bridgechampions-cloud-functions/yearly-upgrade), which checks the member's sign-in token.
//
// Feature lines are Paul's own words from the yearly card on the membership page.

const ENDPOINT = "https://us-central1-bridgechampions.cloudfunctions.net/yearlyUpgrade";
const CARD_PORTAL = "https://billing.stripe.com/p/login/9B68wP1gE0EhcLq8tt3Je00";
const isLocalhost = typeof window !== "undefined" && window.location.hostname === "localhost";

const niceDate = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
const brandName = (b) =>
  ({ visa: "Visa", mastercard: "Mastercard", amex: "American Express" }[String(b || "").toLowerCase()] || "Card");

// Localhost-only previews of each screen (?preview=one-click|done|declined|manual|yearly), so the
// page can be looked at without a real account. Never active on the live site.
const PREVIEWS = {
  "one-click": { ok: true, state: "one-click", amount: 477.74, list: 500, credit: 22.26, card: { brand: "visa", last4: "4242" }, renewal: "2027-09-26T00:00:00Z", carriedDays: 0, trial: false, currentTier: "basic" },
  done: { ok: true, state: "done", renewal: "2027-09-26T00:00:00Z", charged: 477.74, credit: 22.26, carriedDays: 0 },
  declined: { ok: false, state: "declined" },
  manual: { ok: true, state: "manual" },
  yearly: { ok: true, state: "yearly" },
};

async function call(action) {
  const user = firebase.auth().currentUser;
  if (!user) return { state: "signin" };
  const token = await user.getIdToken();
  const r = await fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action }),
  });
  return r.json();
}

export function YearlyFeatures() {
  return (
    <ul className="gy-features">
      <li><span className="gy-tick" aria-hidden="true">✓</span><span><strong>Everything in Premium</strong> for a full year, plus:</span></li>
      <li><span className="gy-star" aria-hidden="true">★</span><span><strong>Personalised system review from Paul</strong> - check how you can improve your system, give suggestions for ways to simplify and improve, which conventions to add or remove, and how to improve your signalling</span></li>
      <li><span className="gy-star" aria-hidden="true">★</span><span><strong>Priority queue for requests</strong> - your questions go to the front of the line, and are regularly turned into new articles and videos on the site, so they get answered comprehensively</span></li>
      <li className="gy-bonus"><span className="gy-star" aria-hidden="true">★</span><span><strong>Bonus ladder points to catch up for missed daily points</strong></span></li>
    </ul>
  );
}

function GoYearly({ uid, authReady, location }) {
  const preview = isLocalhost && new URLSearchParams(location.search).get("preview");
  const [status, setStatus] = useState(preview ? PREVIEWS[preview] || null : null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(preview === "done" || preview === "declined" ? PREVIEWS[preview] : null);

  useEffect(() => {
    if (preview || !authReady) return;
    if (!uid) { setStatus({ state: "signin" }); return; }
    let alive = true;
    call("status")
      .then((s) => { if (!alive) return; if (s && s.state === "checkout") window.location.replace("/membership?plan=yearly"); else setStatus(s); })
      .catch(() => alive && setStatus({ state: "error" }));
    return () => { alive = false; };
  }, [uid, authReady, preview]);

  const confirm = useCallback(async () => {
    if (preview) { setResult(PREVIEWS.done); return; }
    setBusy(true);
    try {
      setResult(await call("confirm"));
    } catch (e) {
      setResult({ state: "error" });
    }
    setBusy(false);
  }, [preview]);

  const shell = (children) => (
    <div className="gy-page">
      <div className="gy-card">
        <span className="gy-badge">Best value</span>
        {children}
      </div>
    </div>
  );

  if (result && (result.state === "done" || result.state === "check")) {
    return shell(
      <>
        <div className="gy-doneIcon" aria-hidden="true">✓</div>
        <h1 className="gy-title">You're on Premium Yearly</h1>
        <p className="gy-lead">Thank you. A${Number(result.charged || 500).toFixed(2)} has been charged and your receipt is on its way by email.</p>
        <div className="gy-summary">
          {result.credit > 0 && <div><span>Taken off</span><strong>A${result.credit.toFixed(2)} for the unused part of your month</strong></div>}
          {result.carriedDays > 0 && <div><span>Added on</span><strong>{result.carriedDays} days</strong></div>}
          <div><span>Renews</span><strong>{niceDate(result.renewal)}</strong></div>
        </div>
        <Link to="/game-plan" className="gy-btn gy-btn--primary">Go to your Game Plan</Link>
      </>
    );
  }
  if (result && result.state === "declined") {
    return shell(
      <>
        <h1 className="gy-title">Your card was declined</h1>
        <p className="gy-lead">Nothing was charged and your plan hasn't changed.</p>
        <p>You can update your card here, then come back and try again.</p>
        <a className="gy-btn gy-btn--primary" href={CARD_PORTAL} target="_blank" rel="noopener noreferrer">Update my card</a>
        <button className="gy-btn gy-btn--ghost" onClick={() => setResult(null)}>Try again</button>
      </>
    );
  }
  if (result && result.state !== "yearly" && result.ok === false && result.state !== "manual") {
    return shell(
      <>
        <h1 className="gy-title">That didn't go through</h1>
        <p className="gy-lead">Nothing was charged unless you receive a receipt by email. Please email me at <a href="mailto:paul@bridgechampions.com?subject=Switching%20to%20yearly">paul@bridgechampions.com</a> and I'll sort it out.</p>
      </>
    );
  }

  if (!status) {
    return shell(<p className="gy-loading">Checking your membership…</p>);
  }
  if (status.state === "signin") {
    return shell(
      <>
        <h1 className="gy-title">Premium Yearly</h1>
        <p className="gy-lead">Please log in first, then come back to this page.</p>
        <Link to="/login" className="gy-btn gy-btn--primary">Log in</Link>
      </>
    );
  }
  if (status.state === "yearly" || (result && result.state === "yearly")) {
    return shell(
      <>
        <h1 className="gy-title">You're already on yearly</h1>
        <p className="gy-lead">Nothing to do - you have the best-value plan.</p>
        <Link to="/game-plan" className="gy-btn gy-btn--primary">Go to your Game Plan</Link>
      </>
    );
  }
  if (status.state === "manual" || (result && result.state === "manual")) {
    return shell(
      <>
        <h1 className="gy-title">Premium Yearly</h1>
        <YearlyFeatures />
        <p className="gy-lead">Your membership is set up a little differently, so I'll switch you over myself.</p>
        <a className="gy-btn gy-btn--primary" href="mailto:paul@bridgechampions.com?subject=Switch%20me%20to%20yearly&body=Hi%20Paul%2C%20I%27d%20like%20to%20switch%20to%20yearly.">Email me to switch</a>
      </>
    );
  }
  if (status.state !== "one-click") {
    return shell(
      <>
        <h1 className="gy-title">Premium Yearly</h1>
        <p className="gy-lead">Something went wrong checking your membership. Please email me at <a href="mailto:paul@bridgechampions.com?subject=Switching%20to%20yearly">paul@bridgechampions.com</a>.</p>
      </>
    );
  }

  const fromBasic = status.currentTier !== "premium";
  return shell(
    <>
      <h1 className="gy-title">Switch to Premium Yearly</h1>
      <div className="gy-price">
        <span className="gy-was">A$600</span> <span className="gy-now">A$500</span> <span className="gy-per">a year</span>
      </div>
      <p className="gy-pitch">Best value, and the plan where I can work with you the most.</p>
      <YearlyFeatures />

      <div className="gy-summary">
        <div><span>Charged today</span><strong>A${status.amount.toFixed(2)} to {brandName(status.card.brand)} ending {status.card.last4}</strong></div>
        {status.credit > 0 && (
          <div className="gy-note">That is A$500 for the year, less A${status.credit.toFixed(2)} for the unused part of your current month.</div>
        )}
        <div><span>Your year runs until</span><strong>{niceDate(status.renewal)}</strong></div>
        {status.carriedDays > 0 && (
          <div className="gy-note">
            {status.trial
              ? `The ${status.carriedDays} days left on your free trial are added on to the end.`
              : `The ${status.carriedDays} extra days on your account are added on to the end.`}
          </div>
        )}
        {fromBasic && <div className="gy-note">You move up to Premium straight away.</div>}
      </div>

      <button className="gy-btn gy-btn--primary gy-btn--big" onClick={confirm} disabled={busy}>
        {busy ? "Processing…" : `Confirm and pay A$${status.amount.toFixed(2)}`}
      </button>
      <Link to="/game-plan" className="gy-btn gy-btn--ghost">Not now</Link>
      <p className="gy-fine">Changed your mind? Tell me within 7 days and I&rsquo;ll refund you in full, no questions asked.</p>
    </>
  );
}

const mapStateToProps = (state) => ({ uid: state.auth.uid, authReady: state.auth.authReady });
export default connect(mapStateToProps)(GoYearly);
